import { useQuery } from '@tanstack/react-query';
import type { AuditLogDto, PaginatedDto } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { EmptyState, ErrorState, SkeletonRows } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatDateTime } from '../lib/format';

/** Human labels for the dotted verbs written by recordAudit(). */
const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.totp_enrolled': 'Set up two-factor',
  'auth.password_changed': 'Changed password',
  'school.created': 'Onboarded school',
  'school.suspended': 'Suspended school',
  'school.reactivated': 'Reactivated school',
  'school.plan_changed': 'Changed plan',
  'school.modules_changed': 'Changed features',
  'school.pricing_changed': 'Changed pricing',
  'invoice.marked_paid': 'Marked invoice paid',
  'incident.updated': 'Updated incident',
};

export function AuditPage() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['audit'],
    queryFn: () => api.get<PaginatedDto<AuditLogDto>>('/api/audit?pageSize=100'),
  });

  return (
    <>
      <PageHeader title="Audit log" meta={data ? `${data.total} recorded actions` : 'Loading…'} />

      <div className="page">
        {error ? (
          <ErrorState message={error instanceof ApiRequestError ? error.message : 'Could not load the audit log'} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows count={6} />
        ) : data.items.length === 0 ? (
          <div className="card card--flush">
            <EmptyState title="Nothing logged yet" message="Platform actions will appear here as they happen." />
          </div>
        ) : (
          <div className="card card--flush">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 190 }}>When</th>
                  <th style={{ width: 220 }}>Action</th>
                  <th style={{ width: 240 }}>Who</th>
                  <th>Where / what</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((entry) => (
                  <tr key={entry.id} style={{ cursor: 'default' }}>
                    <td className="mono caption">{formatDateTime(entry.createdAt)}</td>
                    <td style={{ fontSize: 14, fontWeight: 700 }}>{ACTION_LABELS[entry.action] ?? entry.action}</td>
                    <td className="caption">{entry.actorEmail ?? 'system'}</td>
                    <td className="caption">
                      {entry.schoolName ?? entry.targetType}
                      {entry.metadata ? (
                        <span className="mono" style={{ marginLeft: 8, opacity: 0.75 }}>
                          {summarise(entry.metadata)}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function summarise(metadata: Record<string, unknown>): string {
  return Object.entries(metadata)
    .filter(([, value]) => value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0))
    .slice(0, 3)
    .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join('/') : String(value)}`)
    .join(' · ');
}
